// src/slices/policiesSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { UIPolicy } from '../services/types';
import { policiesService } from '../services/policies.service';

type Status = 'idle' | 'loading' | 'succeeded' | 'failed';

interface PoliciesState {
  // full dataset (used by selectors/KPIs)
  items: UIPolicy[];
  status: Status;
  error?: string;

  // pagination channel (for tables)
  pageData: UIPolicy[]; // current page items
  page: number; // 1-based
  pageSize: number; // items per page
  total: number; // total items
  pageLoading: boolean;
}

const initialState: PoliciesState = {
  items: [],
  status: 'idle',
  pageData: [],
  page: 1,
  pageSize: 25,
  total: 0,
  pageLoading: false,
};

/** Full list (unchanged, for backward compatibility) */
export const fetchPolicies = createAsyncThunk<UIPolicy[]>('policies/fetchAll', async () => {
  return await policiesService.list(); // zero-arg service returning ALL items
});

/** Paged list — loads ALL once (or reuses items) and slices client-side */
export const fetchPoliciesPage = createAsyncThunk<
  { items: UIPolicy[]; total: number; page: number; pageSize: number },
  { page?: number; pageSize?: number } | void
>('policies/fetchPage', async (args, { getState }) => {
  const state = getState() as { policies: PoliciesState };
  const page = args?.page ?? state.policies.page ?? 1;
  const pageSize = args?.pageSize ?? state.policies.pageSize ?? 25;
  const offset = (page - 1) * pageSize;

  const all = state.policies.items.length > 0 ? state.policies.items : await policiesService.list();

  const total = all.length;
  const items = all.slice(offset, offset + pageSize);

  return { items, total, page, pageSize };
});

const policiesSlice = createSlice({
  name: 'policies',
  initialState,
  reducers: {
    setPoliciesPage(state, action: PayloadAction<number>) {
      state.page = Math.max(1, action.payload);
    },
    setPoliciesPageSize(state, action: PayloadAction<number>) {
      state.pageSize = Math.max(1, action.payload);
      state.page = 1; // reset to first page when size changes
    },
    resetPoliciesPagination(state) {
      state.page = 1;
      state.pageSize = initialState.pageSize;
      state.total = 0;
      state.pageData = [];
      state.pageLoading = false;
    },
  },
  extraReducers: (b) => {
    // full list
    b.addCase(fetchPolicies.pending, (s) => {
      s.status = 'loading';
      s.error = undefined;
    })
      .addCase(fetchPolicies.fulfilled, (s, a: PayloadAction<UIPolicy[]>) => {
        s.status = 'succeeded';
        s.items = a.payload;
        // optionally seed page data on first full fetch
        if (s.total === 0) {
          s.total = a.payload.length;
          s.pageData = a.payload.slice(0, s.pageSize);
        }
      })
      .addCase(fetchPolicies.rejected, (s, a) => {
        s.status = 'failed';
        s.error = a.error.message;
      });

    // paged channel
    b.addCase(fetchPoliciesPage.pending, (s) => {
      s.pageLoading = true;
      s.error = undefined;
    })
      .addCase(
        fetchPoliciesPage.fulfilled,
        (
          s,
          a: PayloadAction<{
            items: UIPolicy[];
            total: number;
            page: number;
            pageSize: number;
          }>,
        ) => {
          s.pageLoading = false;
          s.pageData = a.payload.items;
          s.total = a.payload.total;
          s.page = a.payload.page;
          s.pageSize = a.payload.pageSize;
        },
      )
      .addCase(fetchPoliciesPage.rejected, (s, a) => {
        s.pageLoading = false;
        s.error = a.error.message;
      });
  },
});

export const { setPoliciesPage, setPoliciesPageSize, resetPoliciesPagination } =
  policiesSlice.actions;

export default policiesSlice.reducer;
